import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { programs, SUBJECTS, TOP_COURSES, CATEGORY_ONLINE_INTENT, CATEGORY_FEES_INTENT, CATEGORY_2026_INTENT, LOCALITY_UTILITY_INTENT, AEO_QUESTION_INTENTS, getIntentById } from "@/data/programs";
import { courses, courseCategories, getCourseBySlug } from "@/data/courses";
import { locations, getLocationBySlug } from "@/data/locations";
import { Search, MapPin, BookOpen, ArrowRight, Sparkles, Layers } from "lucide-react";

const SITE = "https://vedantiacademy.com";

function programLabel(p) {
  const loc = getLocationBySlug(p.locationSlug);
  if (!loc) return p.slug;
  if (p.type === "intent-course-location") {
    const c = getCourseBySlug(p.courseSlug);
    const intent = getIntentById(p.intent);
    return intent && c ? intent.h1(c, loc) : p.slug;
  }
  if (p.type === "category-location") {
    const cat = courseCategories.find((x) => x.id === p.categoryId);
    return cat ? `Best ${cat.name} Coaching in ${loc.name}` : p.slug;
  }
  if (p.type === "subject-location") {
    const s = SUBJECTS.find((x) => x.slug === p.subjectSlug);
    return s ? `${s.name} Coaching in ${loc.name}` : p.slug;
  }
  if (p.type === "year-course-location") {
    const c = getCourseBySlug(p.courseSlug);
    return c ? `${c.name} 2026 Coaching in ${loc.name}` : p.slug;
  }
  if (p.type === "howto-course-location") {
    const c = getCourseBySlug(p.courseSlug);
    return c ? `How to Crack ${c.name} from ${loc.name}` : p.slug;
  }
  if (p.type === "category-online-location") {
    const cat = courseCategories.find((x) => x.id === p.categoryId);
    return cat ? CATEGORY_ONLINE_INTENT.h1(cat, loc) : p.slug;
  }
  if (p.type === "category-fees-location") {
    const cat = courseCategories.find((x) => x.id === p.categoryId);
    return cat ? CATEGORY_FEES_INTENT.h1(cat, loc) : p.slug;
  }
  if (p.type === "category-2026-location") {
    const cat = courseCategories.find((x) => x.id === p.categoryId);
    return cat ? CATEGORY_2026_INTENT.h1(cat, loc) : p.slug;
  }
  if (p.type === "locality-utility") {
    return LOCALITY_UTILITY_INTENT.h1(loc);
  }
  if (p.type === "aeo-question") {
    const c = getCourseBySlug(p.courseSlug);
    const aeo = AEO_QUESTION_INTENTS.find((a) => a.id === p.aeoIntent);
    return c && aeo ? aeo.h1(c, loc) : p.slug;
  }
  return p.slug;
}

const TYPE_LABELS = {
  "intent-course-location": "Course × Location",
  "category-location": "Best Category",
  "category-online-location": "Online Category",
  "category-fees-location": "Category Fees",
  "category-2026-location": "Category 2026",
  "subject-location": "Subject × Location",
  "year-course-location": "2026 Course Batches",
  "howto-course-location": "Strategy Guides",
  "locality-utility": "Free Mock Tests",
  "aeo-question": "AEO Questions",
};

const PAGE_SIZE = 60;

export default function ProgramsIndexPage() {
  const [query, setQuery] = useState("");
  const [type, setType] = useState("all");
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const tokens = q.split(/\s+/).filter(Boolean);
    return programs.filter((p) => {
      if (type !== "all" && p.type !== type) return false;
      if (!tokens.length) return true;
      const haystack = p.slug.toLowerCase();
      return tokens.every((t) => haystack.includes(t));
    });
  }, [query, type]);

  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const visible = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const title = "All Exam Coaching Programs | Vedanti Academy";
  const typeLabel = type !== "all" ? TYPE_LABELS[type] : null;
  const pageSuffix = safePage > 1 ? ` — page ${safePage}` : "";
  const desc = typeLabel
    ? `Browse Vedanti Academy ${typeLabel} coaching programs${pageSuffix}. Banking, SSC, Railway, MBA & Govt exam prep across Badlapur, Ambernath, Ulhasnagar, Karjat, Neral, Vangni & Khopoli. Call +91 8308776505.`
    : `Browse all ${total} Vedanti Academy coaching programs${pageSuffix} for Banking, SSC, Railway, MBA & Govt exams across Badlapur, Ambernath, Ulhasnagar, Karjat, Neral, Vangni & Khopoli. Free demo. Call +91 8308776505.`;
  const url = `${SITE}/programs`;

  // ItemList JSON-LD for the index (sample first 50 to keep doc size bounded)
  const itemListLd = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "Vedanti Academy Programs",
    numberOfItems: programs.length,
    itemListElement: programs.slice(0, 50).map((p, i) => ({
      "@type": "ListItem",
      position: i + 1,
      url: `${SITE}/programs/${p.slug}`,
      name: programLabel(p),
    })),
  };

  return (
    <div data-testid="programs-index-page">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={desc} />
        <link rel="canonical" href={url} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={desc} />
        <meta property="og:url" content={url} />
        <script type="application/ld+json">{JSON.stringify(itemListLd)}</script>
      </Helmet>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-vedanti-navy"></div>
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(249,115,22,0.2)_0%,_transparent_55%)]"></div>
        <div className="absolute bottom-0 left-0 right-0 h-1.5 bg-gradient-to-r from-vedanti-orange via-vedanti-mango to-vedanti-orange"></div>
        <div className="container-main py-14 md:py-20 relative z-10">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles size={16} className="text-vedanti-mango" />
            <span className="text-vedanti-mango text-sm font-bold uppercase tracking-wider">Programs Hub</span>
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white leading-tight">
            All <span className="text-transparent bg-clip-text bg-gradient-to-r from-vedanti-orange to-vedanti-mango">Vedanti Academy</span> Programs
          </h1>
          <p className="text-slate-300 mt-4 text-lg max-w-3xl">
            Discover the right course × location combination for your goal — Banking, SSC, Railway, MBA, Insurance and Govt exam coaching across {locations.length}+ locations &amp; localities in Mumbai, Thane, Raigad and Pune.
          </p>

          {/* Search + filter */}
          <div className="mt-8 grid sm:grid-cols-3 gap-3 max-w-3xl">
            <div className="relative sm:col-span-2">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={query}
                onChange={(e) => { setQuery(e.target.value); setPage(1); }}
                placeholder="Search ‘IBPS PO Kalyan’, ‘best banking Thane’ …"
                className="w-full pl-9 pr-3 py-3 rounded-xl bg-white/95 text-vedanti-navy placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-vedanti-orange"
                data-testid="programs-search-input"
              />
            </div>
            <select
              value={type}
              onChange={(e) => { setType(e.target.value); setPage(1); }}
              className="px-3 py-3 rounded-xl bg-white/95 text-vedanti-navy focus:outline-none focus:ring-2 focus:ring-vedanti-orange"
              data-testid="programs-type-filter"
            >
              <option value="all">All program types</option>
              {Object.entries(TYPE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
        </div>
      </section>

      {/* Quick browse — by location */}
      <section className="bg-white border-b">
        <div className="container-main py-8">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-vedanti-navy mb-4 flex items-center gap-2">
            <MapPin size={18} className="text-vedanti-orange" /> Browse by location
          </h2>
          <div className="flex flex-wrap gap-2">
            {locations.map((l) => (
              <Link key={l.slug} to={`/locations/${l.slug}`} className="px-3 py-1.5 rounded-full bg-slate-100 hover:bg-vedanti-orange hover:text-white text-sm text-vedanti-navy font-medium transition-all">
                {l.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Quick browse — by category */}
      <section className="bg-slate-50 border-b">
        <div className="container-main py-8">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-vedanti-navy mb-4 flex items-center gap-2">
            <Layers size={18} className="text-vedanti-orange" /> Browse by exam category
          </h2>
          <div className="flex flex-wrap gap-2">
            {courseCategories.map((c) => (
              <Link key={c.id} to={`/programs?type=category-location&category=${c.id}`} onClick={() => { setType("category-location"); setQuery(c.name.split(" ")[0]); setPage(1); }} className="px-3 py-1.5 rounded-full bg-white border border-slate-200 hover:border-vedanti-orange hover:text-vedanti-orange text-sm text-vedanti-navy font-medium transition-all">
                {c.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Listing */}
      <section className="section-padding bg-white">
        <div className="container-main">
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-slate-500">
              Page <strong className="text-vedanti-navy">{safePage}</strong> of {totalPages}
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {visible.map((p) => (
              <Link key={p.slug} to={`/programs/${p.slug}`} className="group bg-slate-50 hover:bg-orange-50 border border-slate-100 hover:border-vedanti-orange/30 rounded-xl p-4 transition-all flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-vedanti-orange to-amber-500 text-white flex-shrink-0 flex items-center justify-center">
                  <BookOpen size={16} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-vedanti-navy group-hover:text-vedanti-orange text-sm leading-snug">{programLabel(p)}</div>
                  <div className="text-xs text-slate-500 mt-1">{TYPE_LABELS[p.type]}</div>
                </div>
                <ArrowRight size={16} className="text-vedanti-orange opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 mt-1" />
              </Link>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-8 flex items-center justify-center gap-2">
              <button onClick={() => setPage(Math.max(1, safePage - 1))} disabled={safePage === 1} className="px-4 py-2 rounded-lg bg-slate-100 text-vedanti-navy font-semibold text-sm disabled:opacity-40 hover:bg-slate-200 transition-all">Previous</button>
              <span className="text-sm text-slate-500">Page {safePage} / {totalPages}</span>
              <button onClick={() => setPage(Math.min(totalPages, safePage + 1))} disabled={safePage === totalPages} className="px-4 py-2 rounded-lg bg-vedanti-orange text-white font-semibold text-sm disabled:opacity-40 hover:bg-amber-500 transition-all">Next</button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
